"use client"

import { createContext, useContext, useEffect, useState, type ReactNode } from "react"
import { AgentCoreClient } from "./client"
import {
  detectFrontendMode,
  resolveInvocationTarget,
  VITE_OVERRIDES,
  type AwsExports,
} from "./config"
import type { AgentName, AgentPattern } from "./types"

/**
 * React context holding the singleton AgentCoreClient instance.
 *
 * The client is resolved once per active assistant from aws-exports.json plus
 * the current frontend mode, and shared across the entire component tree. Any
 * component that needs to talk to the agent runtime (chat invocation, system
 * prompt fetch, etc.) consumes this context rather than constructing its own
 * client.
 */
const AgentCoreClientContext = createContext<AgentCoreClient | null>(null)

interface AgentCoreClientProviderProps {
  children: ReactNode
  /**
   * The assistant the user currently has open (supplied by the Chat page).
   *
   * Typed optional only so the required-ID guard is reachable and type-correct;
   * the provider never defaults it, so a missing value is a configuration error
   * rather than a silent fallback.
   */
  activeAssistantId?: AgentName
  /** Surfaces configuration failures in the Chat page's existing error banner. */
  onError: (message: string) => void
}

/**
 * Provider that resolves exactly one invocation target for the active assistant
 * and exposes the constructed AgentCoreClient to all descendants via context.
 *
 * Resolution is mode-specific: `detectFrontendMode` classifies the run and
 * `resolveInvocationTarget` reads only that mode's single source. On any
 * failure the provider leaves `client` null (so no invocation is possible) and
 * reports the targeted message through `onError`.
 *
 * @param children - Child components that may consume the client.
 * @param activeAssistantId - The Canonical_Assistant_ID currently open.
 * @param onError - Error callback for configuration failures.
 */
export function AgentCoreClientProvider({
  children,
  activeAssistantId,
  onError,
}: AgentCoreClientProviderProps) {
  const [client, setClient] = useState<AgentCoreClient | null>(null)

  useEffect(() => {
    let cancelled = false

    // Clear any prior client before re-resolving so a changed assistant's old
    // client cannot remain usable while the new target is loading.
    setClient(null)

    async function init() {
      try {
        // A missing active assistant is an error, never a survey-variable
        // fallback — the provider does not default the ID.
        if (!activeAssistantId) {
          throw new Error("Active assistant ID is required.")
        }

        const response = await fetch("/aws-exports.json")
        if (!response.ok) {
          throw new Error("Failed to load configuration")
        }
        const config: AwsExports = await response.json()

        // Classify the run into exactly one mode, then resolve the single
        // target for the active assistant from only that mode's source. A
        // missing/empty entry throws a targeted Error caught below.
        const mode = detectFrontendMode(import.meta.env)
        const target = resolveInvocationTarget({
          mode,
          activeAssistantId,
          config,
          overrides: VITE_OVERRIDES,
        })

        // Map the discriminated ResolvedTarget onto the unchanged single-target
        // client constructor: exactly one of runtimeArn / localAgentUrl is set.
        const c = new AgentCoreClient({
          runtimeArn: target.kind === "runtimeArn" ? target.runtimeArn : "",
          region: config.awsRegion ?? "ap-southeast-2",
          pattern: (config.agentPattern ?? "strands-agentcore-apps") as AgentPattern,
          localAgentUrl: target.kind === "localUrl" ? target.localAgentUrl : undefined,
        })

        if (!cancelled) setClient(c)
      } catch (err) {
        const msg = err instanceof Error ? err.message : "Unknown error"
        // Leave client null so no invocation is possible, and surface the
        // targeted message through the required error callback.
        if (!cancelled) onError(`Configuration error: ${msg}`)
        console.error("Failed to load agent configuration:", err)
      }
    }

    init()
    return () => {
      cancelled = true
    }
  }, [activeAssistantId, onError])

  return (
    <AgentCoreClientContext.Provider value={client}>
      {children}
    </AgentCoreClientContext.Provider>
  )
}

/**
 * Hook to access the shared AgentCoreClient instance.
 *
 * Returns null while the client is still loading from configuration, or when
 * resolution failed (so no invocation is possible).
 *
 * @returns The AgentCoreClient, or null if not yet initialized.
 */
export function useAgentCoreClient(): AgentCoreClient | null {
  return useContext(AgentCoreClientContext)
}
